import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  differenceInCalendarDays,
  addDays,
  format,
} from "date-fns";
import { useTheme, Box } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import { startOfDay } from "date-fns";
import { assignmentToGanttBar, cascadeBars } from "./helpers";
import GanttBar from "./GanttBar";
import NowLine from "./NowLine";
import api from 'state/axios';
import toast from 'react-hot-toast';
import ConfirmDialog from 'components/ConfirmDialog';


const ROW_PADDING = 4;

const BottomRightPanel = ({
    syncX,
    timelineScrollRef,
    topRightScrollRef,
    bottomRightScrollRef,
    rowHeight = 40,
    cellWidth = 8,
    startDate,
    endDate,
    unassignments,
    selectedBarId,
    onBarClick,
    onBarDoubleClick,
    canDelete = false,
    onDeleted,
}) => {
    const theme = useTheme();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { setNodeRef: setPoolRef, isOver } = useDroppable({
        id: 'unassigned-pool',
        data: { dropType: 'UNASSIGNED_POOL' },
    });

    // Combine scroll ref + droppable ref trên cùng 1 node
    const setContainerRef = useCallback((node) => {
        setPoolRef(node);
        bottomRightScrollRef.current = node;
    }, [setPoolRef, bottomRightScrollRef]);

    const handleScroll = useCallback((e) => {
        syncX(e.target, [
            timelineScrollRef?.current,
            topRightScrollRef?.current,
        ]);
    }, [syncX, timelineScrollRef, topRightScrollRef]);

    /* ---------- DELETE SELECTED BAR ON DELETE KEY ---------- */
    const selectedAssignment = useMemo(() => {
        if (!selectedBarId?.startsWith('unassigned-')) return null;
        const id = selectedBarId.slice('unassigned-'.length);
        return unassignments.find(a => String(a._id) === id) || null;
    }, [selectedBarId, unassignments]);

    useEffect(() => {
        if (!selectedAssignment || !canDelete) return;
        const onKeyDown = (e) => {
            if (e.key !== 'Delete') return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
            setConfirmOpen(true);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedAssignment, canDelete]);

    const handleDeleteConfirm = async () => {
        if (!selectedAssignment) return;
        try {
            await api.delete(`/assignments/delete/${selectedAssignment._id}`);
            toast.success("Assignment deleted.");
            onDeleted?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Delete failed.");
        }
    };

    /* ---------- TIMELINE WIDTH ---------- */
    const datesCount = useMemo(() => {
        if (!startDate || !endDate) return 0;
        return differenceInCalendarDays(addDays(endDate, 1), startDate);
    }, [startDate, endDate]);

    const timelineWidth = (datesCount + 1) * 24 * cellWidth;

    /* ---------- GRID BACKGROUND ---------- */
    const gridLines = useMemo(() => {
        const d = theme.palette.divider;
        const dayW = cellWidth * 24;
        return {
            backgroundImage: [
                `linear-gradient(to right, ${d} 1px, transparent 1px)`,
                `linear-gradient(to bottom, ${d} 1px, transparent 1px)`,
            ].join(', '),
            backgroundSize: `${dayW}px 100%, 100% ${rowHeight}px`,
        };
    }, [cellWidth, rowHeight, theme.palette.divider]);

    /* ---------- TO GANTT BARS ---------- */
    const bars = useMemo(
        () =>
            unassignments
                .map(a => assignmentToGanttBar(a, startDate))
                .filter(Boolean),
        [unassignments, startDate]
    );

    /* ---------- CASCADE ---------- */
    const { bars: cascadedBars, laneCount } = useMemo(
        () => cascadeBars(bars),
        [bars]
    );

    const cascadeHeight = laneCount * rowHeight + ROW_PADDING * 2;


    return (
        <>
        <Box
            ref={setContainerRef}
            onScroll={handleScroll}
            sx={{
                position: "relative",
                height: "100%",
                flexGrow: 1,
                overflow: "auto",
                bgcolor: isOver ? 'action.hover' : 'background.default',
                transition: 'background-color 0.15s',
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: timelineWidth,
                    height: cascadeHeight,
                    minHeight: '100%',
                    ...gridLines,
                }}
            >
                <NowLine
                    baseDate={startDate && startOfDay(new Date(startDate))}
                    datesCount={datesCount}
                    cellWidth={cellWidth}
                />
                {cascadedBars?.map((bar) => (
                    <GanttBar
                        key={bar.id}
                        bar={{
                            ...bar,
                            uniqueId: `unassigned-${bar.id}`,
                            dragSource: 'UNASSIGNED_POOL',
                        }}
                        cellWidth={cellWidth}
                        rowHeight={rowHeight}
                        isActive={selectedBarId === `unassigned-${bar.id}`}
                        isOptimistic={!!bar.__optimistic}
                        onClick={onBarClick}
                        onDoubleClick={onBarDoubleClick}
                    />
                ))}
            </Box>
        </Box>

        <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleDeleteConfirm}
            title="Confirm?"
            message={selectedAssignment
                ? `Delete shift "${selectedAssignment.shiftId?.shiftName || selectedAssignment.shiftId?.shiftCode || ''}" on ${format(new Date(selectedAssignment.date), 'dd/MM/yyyy')}?`
                : ''}
            confirmText="Delete"
            confirmColor="error"
        />
        </>
    );
};

export default React.memo(BottomRightPanel);
